/**
 * GT Repayments — Driver matching and transaction time inference.
 * Pure logic module — no Supabase calls.
 */

import type { BankStatementRow, GtNotification } from './sbt-pdf';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DriverOption {
  id: number;
  repaymentTag: string | null;
  name: string;
  contractId: number | null;
}

export interface MatchResult {
  driverId: number;
  driverName: string;
  contractId: number | null;
  confidence: 'high' | 'low';
}

// ─── Driver Matching ─────────────────────────────────────────────────────────

/**
 * Match a transaction description to a driver using their repayment_tag.
 *
 * Matching rules (derived from ~11k real GHS assignments):
 * - For multi-word tags: ALL words must appear in the description.
 * - Each word is matched with word-boundary awareness using separators
 *   found in bank descriptions: space, comma, slash, underscore, pipe, colon, dot.
 * - Single-char words in a tag are ignored (noise).
 * - Tags ≤3 chars are skipped entirely to avoid false positives.
 * - Scoring: most matched words wins (more specific tags preferred).
 */
export function matchDriverByTag(
  description: string,
  drivers: DriverOption[]
): MatchResult | null {
  if (!description) return null;

  const descLower = description.toLowerCase();
  let bestMatch: DriverOption | null = null;
  let bestWordScore = 0;
  let bestHasContiguous = false;
  let bestTagLen = 0;

  // Characters that act as word separators in bank descriptions
  const SEP = `[\\s,/_|:.\\-]`;

  for (const driver of drivers) {
    if (!driver.repaymentTag) continue;

    const tag = driver.repaymentTag.trim().toLowerCase();
    if (tag.length <= 3) continue; // Skip overly short tags

    // Split tag into words. Keep words ≥2 chars for multi-word tags,
    // ≥4 chars for single-word tags (avoids noise from fragments like "1")
    const allWords = tag.split(/\s+/).filter((w) => w.length > 1);
    if (allWords.length === 0) continue;
    let tagWords = allWords.length === 1
      ? allWords.filter((w) => w.length >= 4)  // single-word tags need ≥4 chars
      : allWords;                               // multi-word: keep all ≥2 char words
    if (tagWords.length === 0) continue;

    // Remove redundant words that are substrings of longer words in the same tag
    // e.g., "EMMANUEL EMM" → "emm" is a substring of "emmanuel", so drop "emm"
    tagWords = tagWords.filter((w) =>
      !tagWords.some((other) => other !== w && other.length > w.length && other.includes(w))
    );

    let allMatch = true;
    let matchedWords = 0;

    for (const word of tagWords) {
      const escaped = escapeRegex(word);

      // Allow the word to appear:
      // 1. At a separator boundary (both sides)
      // 2. At start/end of string
      // 3. As a prefix of a longer name (e.g., "mens" → "MENSAH")
      //    This is required because GIP concatenates names like "MICHAELDARKO"
      const pattern = new RegExp(
        `(?:^|${SEP})${escaped}(?:${SEP}|$)` + // exact word boundary
        `|(?:^|${SEP})${escaped}` +             // prefix match (word at separator then continues)
        `|${escaped}(?:${SEP}|$)`,              // suffix match (continues then word at separator)
        'i'
      );

      if (pattern.test(descLower)) {
        matchedWords++;
      } else {
        allMatch = false;
        break;
      }
    }

    if (!allMatch) continue;

    // Tie-breaking: does the full tag appear as a contiguous substring?
    // e.g., "MENSAH EVANS" in "FROM MENSAH EVANS TO" → contiguous
    // vs "Evans Mensah" when description has "MENSAH...EVANS" in different order → not contiguous
    const hasContiguous = descLower.includes(tag);

    // Scoring priority:
    // 1. More matched words (word count)
    // 2. Contiguous substring match (stronger signal)
    // 3. Longer tag length (more specific)
    const isBetter =
      matchedWords > bestWordScore ||
      (matchedWords === bestWordScore && hasContiguous && !bestHasContiguous) ||
      (matchedWords === bestWordScore && hasContiguous === bestHasContiguous && tag.length > bestTagLen);

    if (isBetter) {
      bestWordScore = matchedWords;
      bestHasContiguous = hasContiguous;
      bestTagLen = tag.length;
      bestMatch = driver;
    }
  }

  if (!bestMatch) return null;

  return {
    driverId: bestMatch.id,
    driverName: bestMatch.name,
    contractId: bestMatch.contractId,
    confidence: bestMatch.contractId ? 'high' : 'low',
  };
}

/** Escape special regex characters in a string */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─── GT Email Notification Matching ──────────────────────────────────────────

/**
 * Match a bank statement row to a GT email notification.
 * Uses amount + date + description substring overlap to disambiguate
 * when multiple drivers pay the same amount on the same day.
 */
export function matchGtNotification(
  bankRow: BankStatementRow,
  gtNotifications: GtNotification[],
  alreadyMatched: Set<string>
): GtNotification | null {
  const bankAmount = bankRow.credit > 0 ? bankRow.credit : bankRow.debit;
  const bankDate = bankRow.valueDate; // e.g., "19Apr2026" or "2026-04-19"

  // Filter candidates by amount match
  const candidates = gtNotifications.filter((n) => {
    if (alreadyMatched.has(n.docNumber)) return false;
    if (Math.abs(n.amount - bankAmount) > 0.01) return false;
    return true;
  });

  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  // Multiple candidates with same amount — disambiguate by description overlap
  const bankRemarks = bankRow.remarks.toLowerCase();

  let bestCandidate: GtNotification | null = null;
  let bestOverlap = 0;

  for (const candidate of candidates) {
    if (!candidate.description) continue;

    // Extract key words from the email description to match against bank remarks
    const emailDesc = candidate.description.toLowerCase();
    const words = emailDesc.split(/\s+/).filter((w) => w.length > 3);

    let overlap = 0;
    for (const word of words) {
      if (bankRemarks.includes(word)) overlap++;
    }

    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestCandidate = candidate;
    }
  }

  // Require at least 2 matching words for confidence
  return bestOverlap >= 2 ? bestCandidate : candidates[0];
}

// ─── Transaction Time Inference ──────────────────────────────────────────────

/**
 * Infer the transaction time for a bank statement row.
 * Priority:
 * 1. GT email match → use email time
 * 2. No match → use the time from the closest neighboring bank row that HAS a match
 * 3. Final fallback → null
 */
export function inferTransactionTime(
  bankRow: BankStatementRow,
  matchedNotification: GtNotification | null,
): string | null {
  // Only use time from a directly matched GT email notification.
  // No neighbor guessing — only 100% confident matches.
  return matchedNotification?.time ?? null;
}

// ─── Duplicate Detection ─────────────────────────────────────────────────────

export interface ExistingTransaction {
  id: number;
  amount: number;
  transaction_at: string;
  description: string | null;
}

/**
 * Check if a transaction already exists in the database.
 * Matches on amount + transaction_at date + description.
 * NULL descriptions are always treated as unique.
 */
export function checkDuplicateTransaction(
  amount: number,
  transactionAt: string,
  description: string | null,
  existingTxs: ExistingTransaction[]
): ExistingTransaction | null {
  if (!description) return null;

  const targetDate = transactionAt.substring(0, 10);
  const descLower = description.toLowerCase();

  return existingTxs.find((tx) => {
    if (Math.abs(tx.amount - amount) > 0.01) return false;
    if (!tx.description) return false;
    if (tx.transaction_at.substring(0, 10) !== targetDate) return false;
    return tx.description.toLowerCase() === descLower;
  }) || null;
}

// ─── Timestamp Building ──────────────────────────────────────────────────────

const MONTH_INDEX_BY_ABBREVIATION: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

interface DateParts {
  year: number;
  monthIndex: number;
  day: number;
}

/** Month index from a full name or a 3+-letter prefix ("Sept", "AUGUST"), or -1 when unknown. */
function monthIndexOf(monthName: string): number {
  const index = MONTH_INDEX_BY_ABBREVIATION[monthName.slice(0, 3).toLowerCase()];
  return index === undefined ? -1 : index;
}

/** Range-checked parts, or null: one gate for all three accepted shapes. */
function toDateParts(year: number, monthIndex: number, day: number): DateParts | null {
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  return { year, monthIndex, day };
}

/**
 * Split a bank statement date into calendar parts, or null when it is unusable.
 *
 * Deliberately never calls `new Date(str)`: the engine resolves every non-ISO shape against the
 * BROWSER's zone (see `buildTimestamp`), so the string must be taken apart here instead.
 *
 * Accepted shapes, each unambiguous on its own:
 * - day first, month NAMED: "19Apr2026", "19-Apr-2026", "19/Apr/2026", "19 August 2026", "19-Apr-26"
 * - month NAMED first: "Aug 19, 2026", "August 19 2026"
 * - year first, all numeric: "2026-04-19", "2026/04/19"
 *
 * All-numeric shapes with the year LAST ("04/19/2026", "19-4-2026") are refused on purpose: dd/mm
 * and mm/dd are indistinguishable, so accepting them would silently move a transaction by months.
 * They take `buildTimestamp`'s fallback instead, which an operator can see and correct in the
 * review table (the date lands on today) rather than being plausibly, invisibly wrong.
 *
 * A TWO-DIGIT year ("10-Aug-26") is read as 2000+yy, but only in the day-first family, where the
 * spelled-out month already fixes every field: nothing else the string could be. The month-first
 * family keeps its four-digit year because "Aug 2026" — a month and a year, no day, which the
 * callers must reject — would otherwise parse as day 20 of a year 26.
 *
 * The bank's XLS/PDF exports separate day/month/year with SOFT HYPHENS (U+00AD), which the parsers
 * in `sbt-pdf` already rewrite to "-"; the same rewrite is repeated here because callers also pass
 * raw values, including whatever an operator types into the date override input. Runs of whitespace
 * are collapsed to one space for the same reason — a hand-typed or column-padded "10  Aug  2026" is
 * the same date as "10 Aug 2026", and a single optional separator is all the patterns below match.
 */
function parseStatementDate(dateStr: string): DateParts | null {
  const normalized = dateStr.replace(/\u00AD/g, '-').trim().replace(/\s+/g, ' ');

  const yearFirst = normalized.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (yearFirst) {
    return toDateParts(
      parseInt(yearFirst[1], 10),
      parseInt(yearFirst[2], 10) - 1,
      parseInt(yearFirst[3], 10)
    );
  }

  const dayFirst = normalized.match(/^(\d{1,2})[-/ ]?([A-Za-z]{3,})[-/ ]?(\d{4}|\d{2})$/);
  if (dayFirst) {
    const year = parseInt(dayFirst[3], 10);
    return toDateParts(
      dayFirst[3].length === 2 ? 2000 + year : year,
      monthIndexOf(dayFirst[2]),
      parseInt(dayFirst[1], 10)
    );
  }

  const monthFirst = normalized.match(/^([A-Za-z]{3,})[-/ ]?(\d{1,2}),?[-/ ]?(\d{4})$/);
  if (monthFirst) {
    return toDateParts(
      parseInt(monthFirst[3], 10),
      monthIndexOf(monthFirst[1]),
      parseInt(monthFirst[2], 10)
    );
  }

  return null;
}

/** Hours/minutes/seconds from an "h:mm[:ss] AM/PM" string; midnight when absent or unrecognised. */
function parseStatementTime(timeStr: string | null): { hours: number; minutes: number; seconds: number } {
  const match = timeStr?.match(/(\d{1,2}):(\d{2}):?(\d{2})?\s*(AM|PM)/i);
  if (!match) return { hours: 0, minutes: 0, seconds: 0 };

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3] || '0', 10);
  const period = match[4].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return { hours, minutes, seconds };
}

/**
 * Build an ISO timestamp from a bank statement date and optional time string.
 * Date shapes: see `parseStatementDate`. Time format: "8:31:52 PM" (seconds optional).
 *
 * A statement's dates and times are GHANA WALL CLOCK — the bank writes them off its own branch
 * clock — and Ghana runs on UTC+0 all year round, with no DST to shift it. Those parts ARE the UTC
 * parts, so the timestamp must come out identical for every operator whatever zone their browser
 * sits in.
 *
 * That is why the string never reaches `new Date()` and the clock never reaches `setHours()`.
 * Both read the browser's zone: `new Date('10-Aug-2026')` is LOCAL midnight, which from a BST
 * browser is 2026-08-09T23:00:00Z — the previous Ghana day, and the reason five Aug-10 imports sit
 * on Aug 9 in production. (`new Date('2026-08-10')` was right only by accident: ISO date-only
 * strings are defined to parse as UTC.) Parts in, `Date.UTC` out, no zone anywhere.
 *
 * An unparseable date keeps the long-standing fallback: the current instant.
 */
export function buildTimestamp(dateStr: string, timeStr: string | null): string {
  const date = parseStatementDate(dateStr);
  if (!date) return new Date().toISOString();

  const { hours, minutes, seconds } = parseStatementTime(timeStr);
  return new Date(
    Date.UTC(date.year, date.monthIndex, date.day, hours, minutes, seconds, 0)
  ).toISOString();
}

/**
 * The "yyyy-MM-dd" Ghana calendar day a bank statement date names, or null when it is unusable.
 *
 * For the callers that need the DAY rather than the instant: the `transaction_emails` query window
 * and the bank-row ↔ email-notification match, both of which compare against a `value_date` the
 * bank itself wrote in Ghana time.
 *
 * Same hazard as `buildTimestamp`, and the same answer. `new Date(dateStr).toISOString().slice(0,
 * 10)` resolves the string in the BROWSER's zone: from UTC+1 a "10-Aug-2026" row becomes local
 * midnight, i.e. 2026-08-09T23:00:00Z, and yields "2026-08-09" — so the query asks for the wrong
 * window and the match can never fire, leaving every row's real bank time behind. Parts in, string
 * out, no zone anywhere.
 *
 * Deliberately built on the same instant `buildTimestamp` produces, so the day a row is matched on
 * and the day it is stored under can never disagree — including on a rolled-over date such as
 * "31-Apr-2026", which both read as May 1.
 */
export function statementDateToYmd(dateStr: string): string | null {
  const date = parseStatementDate(dateStr);
  if (!date) return null;

  return new Date(Date.UTC(date.year, date.monthIndex, date.day)).toISOString().slice(0, 10);
}
